import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LegalFooterLinks } from '../components/legal/LegalFooterLinks';
import StickyNav from '../components/landing/StickyNav';
import { ComingSoonHeader } from '../components/comingSoon/ComingSoonHeader';
import GlobalBackground from '../components/background/GlobalBackground';
import { IS_COMING_SOON } from '../config/siteMode';
import { slugifyHeading } from './slugify.js';
import styles from './LegalPage.module.scss';

function flattenText(value) {
  if (Array.isArray(value)) {
    return value.map(flattenText).join('');
  }

  if (value && typeof value === 'object' && 'props' in value) {
    return flattenText(value.props?.children);
  }

  return typeof value === 'string' ? value : '';
}

function extractHeadingsFromMarkdown(markdown) {
  return String(markdown || '')
    .split('\n')
    .map((line) => line.match(/^(#{2,3})\s+(.+)$/))
    .filter(Boolean)
    .map((match) => {
      const depth = match[1].length;
      const text = match[2].trim();
      return {
        depth,
        text,
        id: slugifyHeading(text),
      };
    });
}

function extractHeadingsFromDocument(document) {
  if (!document || !Array.isArray(document.sections)) {
    return [];
  }

  return document.sections
    .filter((section) => section?.heading)
    .map((section) => {
      const depth = Number.isFinite(section?.depth) ? section.depth : 2;
      const text = String(section.heading).trim();
      return {
        depth,
        text,
        id: slugifyHeading(text),
      };
    })
    .filter((item) => item.text);
}

function headingRenderer(level) {
  const Tag = `h${level}`;

  return function Heading({ children }) {
    const text = flattenText(children);
    const id = slugifyHeading(text);

    return (
      <Tag id={id}>
        <a href={`#${id}`}>{children}</a>
      </Tag>
    );
  };
}

function renderValue(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value.type === 'link' && value.href && value.label) {
    return (
      <a href={value.href}>
        {value.label}
      </a>
    );
  }

  if (value.type === 'lines' && Array.isArray(value.lines) && value.lines.length > 0) {
    return (
      <span className={styles.valueLines}>
        {value.lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </span>
    );
  }

  return null;
}

function FactsBlock({ rows }) {
  const validRows = Array.isArray(rows)
    ? rows.filter((row) => row?.label && row?.value)
    : [];

  if (validRows.length === 0) {
    return null;
  }

  return (
    <dl className={styles.factsList}>
      {validRows.map((row) => (
        <div key={`${row.label}-${String(row.value?.label || row.value)}`} className={styles.factsRow}>
          <dt>{row.label}</dt>
          <dd>{renderValue(row.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ListBlock({ items = [] }) {
  const validItems = items.filter((item) => typeof item === 'string' && item.trim());
  if (validItems.length === 0) {
    return null;
  }

  return (
    <ul className={styles.docList}>
      {validItems.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function ParagraphsBlock({ items = [] }) {
  const validItems = items.filter((item) => typeof item === 'string' && item.trim());
  if (validItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.paragraphStack}>
      {validItems.map((item) => (
        <p key={item}>{item}</p>
      ))}
    </div>
  );
}

function ProvidersBlock({ items = [] }) {
  const { t } = useTranslation();
  const validItems = items.filter((item) => item?.name && item?.role);
  if (validItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.providerGrid}>
      {validItems.map((provider) => (
        <div key={provider.id || provider.name} className={styles.providerCard}>
          <h3>{provider.name}</h3>
          <dl>
            <div>
              <dt>{t('legal.provider.role', { defaultValue: 'Role' })}</dt>
              <dd>{provider.role}</dd>
            </div>
            <div>
              <dt>{t('legal.provider.data', { defaultValue: 'Data categories' })}</dt>
              <dd>{provider.data}</dd>
            </div>
            <div>
              <dt>{t('legal.provider.location', { defaultValue: 'Location' })}</dt>
              <dd>{provider.location}</dd>
            </div>
            <div>
              <dt>{t('legal.provider.privacy', { defaultValue: 'Privacy information' })}</dt>
              <dd>
                <a href={provider.policyUrl}>
                  {provider.policyUrl}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  );
}

function DocumentBlock({ block }) {
  if (!block?.type) {
    return null;
  }

  if (block.type === 'facts') {
    return <FactsBlock rows={block.rows} />;
  }

  if (block.type === 'list') {
    return <ListBlock items={block.items} />;
  }

  if (block.type === 'paragraphs') {
    return <ParagraphsBlock items={block.items} />;
  }

  if (block.type === 'providers') {
    return <ProvidersBlock items={block.items} />;
  }

  return null;
}

function StructuredLegalDocument({ document, title }) {
  const intro = Array.isArray(document?.intro)
    ? document.intro.filter((item) => typeof item === 'string' && item.trim())
    : [];
  const sections = Array.isArray(document?.sections) ? document.sections : [];

  return (
    <>
      <h1>{title}</h1>
      {document?.updatedLabel ? <p className={styles.updatedLabel}>{document.updatedLabel}</p> : null}
      {intro.length > 0 ? (
        <div className={styles.docIntro}>
          {intro.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      ) : null}

      {sections.map((section) => {
        const sectionHeading = String(section?.heading || '').trim();
        if (!sectionHeading) {
          return null;
        }

        const sectionId = slugifyHeading(sectionHeading);
        const blocks = Array.isArray(section.blocks) ? section.blocks : [];

        return (
          <section key={sectionId} className={styles.docSection}>
            <h2 id={sectionId}>
              <a href={`#${sectionId}`}>{sectionHeading}</a>
            </h2>
            <div className={styles.sectionBlocks}>
              {blocks.map((block, index) => (
                <DocumentBlock
                  key={`${sectionId}-${block?.type || 'block'}-${index + 1}`}
                  block={block}
                />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function resolveTocVisibility({ tocMode, tocItems, hasStructuredDocument }) {
  if (!Array.isArray(tocItems) || tocItems.length === 0 || tocMode === 'none') {
    return {
      desktop: false,
      mobile: false,
    };
  }

  if (tocMode === 'always') {
    return {
      desktop: true,
      mobile: true,
    };
  }

  const desktopThreshold = hasStructuredDocument ? 5 : 4;
  const mobileThreshold = hasStructuredDocument ? 6 : 5;

  return {
    desktop: tocItems.length >= desktopThreshold,
    mobile: tocItems.length >= mobileThreshold,
  };
}

const DEFAULT_STICKY_TOP_PX = 96;
const TOC_ACTIVATION_OFFSET_PX = 24;

function resolveStickyTopOffset() {
  if (typeof window === 'undefined') {
    return DEFAULT_STICKY_TOP_PX;
  }

  const cssValue = window
    .getComputedStyle(window.document.documentElement)
    .getPropertyValue('--marketing-sticky-top');
  const parsedValue = Number.parseFloat(cssValue);

  return Number.isFinite(parsedValue) ? parsedValue : DEFAULT_STICKY_TOP_PX;
}

export function LegalPage({
  title,
  markdown = '',
  document = null,
  tocMode = 'always',
}) {
  const { t } = useTranslation();
  const toc = useMemo(
    () => (document ? extractHeadingsFromDocument(document) : extractHeadingsFromMarkdown(markdown)),
    [document, markdown],
  );
  const tocVisibility = useMemo(
    () =>
      resolveTocVisibility({
        tocMode,
        tocItems: toc,
        hasStructuredDocument: Boolean(document),
      }),
    [document, toc, tocMode],
  );
  const [activeSectionId, setActiveSectionId] = useState('');
  const HeaderComponent = IS_COMING_SOON ? ComingSoonHeader : StickyNav;
  const year = new Date().getFullYear();

  useEffect(() => {
    if (toc.length === 0) {
      setActiveSectionId('');
      return;
    }

    if (typeof window === 'undefined') {
      setActiveSectionId(toc[0].id);
      return;
    }

    const hashId = window.location.hash.replace('#', '');
    if (hashId && toc.some((item) => item.id === hashId)) {
      setActiveSectionId(hashId);
      return;
    }

    setActiveSectionId(toc[0].id);
  }, [toc]);

  useEffect(() => {
    if (typeof window === 'undefined' || toc.length === 0) {
      return undefined;
    }

    let rafId = 0;
    const updateActiveSection = () => {
      rafId = 0;
      const stickyTop = resolveStickyTopOffset();
      const activationTop = stickyTop + TOC_ACTIVATION_OFFSET_PX;
      let nextActiveId = toc[0].id;

      for (const item of toc) {
        const sectionHeading = window.document.getElementById(item.id);
        if (!sectionHeading) {
          continue;
        }

        if (sectionHeading.getBoundingClientRect().top <= activationTop) {
          nextActiveId = item.id;
          continue;
        }

        break;
      }

      const scrolledToBottom =
        window.innerHeight + window.scrollY >= window.document.documentElement.scrollHeight - 2;

      if (scrolledToBottom) {
        nextActiveId = toc[toc.length - 1].id;
      }

      setActiveSectionId((currentValue) => (currentValue === nextActiveId ? currentValue : nextActiveId));
    };

    const requestActiveSectionUpdate = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(updateActiveSection);
    };

    requestActiveSectionUpdate();
    window.addEventListener('hashchange', requestActiveSectionUpdate);
    window.addEventListener('resize', requestActiveSectionUpdate);
    window.addEventListener('scroll', requestActiveSectionUpdate, { passive: true });

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      window.removeEventListener('hashchange', requestActiveSectionUpdate);
      window.removeEventListener('resize', requestActiveSectionUpdate);
      window.removeEventListener('scroll', requestActiveSectionUpdate);
    };
  }, [toc]);

  return (
    <>
      <GlobalBackground />
      <div className={styles.page}>
        <HeaderComponent />

        <main className={styles.main}>
          <div className="container">
            <div className={styles.layout}>
              {tocVisibility.mobile ? (
                <details className={styles.mobileToc}>
                  <summary>{t('legal.tocSummary')}</summary>
                  <nav aria-label={t('legal.tocNavAria')}>
                    <ul className={styles.tocList}>
                      {toc.map((item) => (
                        <li key={item.id} className={item.depth === 3 ? styles.depth3 : ''}>
                          <a
                            href={`#${item.id}`}
                            className={`${styles.tocLink}${activeSectionId === item.id ? ` ${styles.tocLinkActive}` : ''}`}
                            aria-current={activeSectionId === item.id ? 'location' : undefined}
                            onClick={() => setActiveSectionId(item.id)}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </details>
              ) : null}

              <article className={styles.content}>
                {document ? (
                  <StructuredLegalDocument document={document} title={title} />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h2: headingRenderer(2),
                      h3: headingRenderer(3),
                    }}
                  >
                    {markdown}
                  </ReactMarkdown>
                )}
              </article>

              {tocVisibility.desktop ? (
                <aside className={styles.desktopToc}>
                  <div className={styles.desktopTocCard}>
                    <p>{t('legal.tocTitle')}</p>
                    <nav aria-label={t('legal.tocNavAria')}>
                      <ul className={styles.tocList}>
                        {toc.map((item) => (
                          <li key={item.id} className={item.depth === 3 ? styles.depth3 : ''}>
                            <a
                              href={`#${item.id}`}
                              className={`${styles.tocLink}${activeSectionId === item.id ? ` ${styles.tocLinkActive}` : ''}`}
                              aria-current={activeSectionId === item.id ? 'location' : undefined}
                              onClick={() => setActiveSectionId(item.id)}
                            >
                              {item.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </aside>
              ) : null}
            </div>

            <footer className={styles.footer}>
              <LegalFooterLinks />
              <p className={styles.copyright}>{t('legal.copyright', { year })}</p>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
}
